import { type Static, Type } from "@sinclair/typebox"
import { APP_EVENT_REGEX } from "../audit-events"
import {
  CursorQuerySchema,
  createPageSchema,
  IsoDateTimeSchema,
  JsonRecordSchema,
  Nullable,
} from "./common"

// Auditoría: consulta admin e ingesta externa — docs/04 §2-§3 y docs/03 §3.

export const AuditActorTypeSchema = Type.Union([
  Type.Literal("user"),
  Type.Literal("client"),
  Type.Literal("system"),
])
export type AuditActorType = Static<typeof AuditActorTypeSchema>

export const AuditSourceSchema = Type.Union([Type.Literal("internal"), Type.Literal("external")])
export type AuditSource = Static<typeof AuditSourceSchema>

export const AuditLogSchema = Type.Object({
  id: Type.String(),
  occurred_at: IsoDateTimeSchema,
  actor_type: AuditActorTypeSchema,
  actor_id: Nullable(Type.String()),
  domain_id: Nullable(Type.String()),
  source: AuditSourceSchema,
  // Notación punto: internos `auth.login.failed`, externos `app.*`
  event: Type.String(),
  target_type: Nullable(Type.String()),
  target_id: Nullable(Type.String()),
  ip: Nullable(Type.String()),
  user_agent: Nullable(Type.String()),
  data: JsonRecordSchema,
})
export type AuditLog = Static<typeof AuditLogSchema>

// GET /admin/audit?event=&actor=&domain=&from=&to=&cursor= (export CSV vía Accept: text/csv)
export const ListAuditQuerySchema = Type.Composite([
  CursorQuerySchema,
  Type.Object({
    event: Type.Optional(Type.String()),
    actor: Type.Optional(Type.String()),
    domain: Type.Optional(Type.String()),
    from: Type.Optional(IsoDateTimeSchema),
    to: Type.Optional(IsoDateTimeSchema),
    source: Type.Optional(AuditSourceSchema),
  }),
])
export type ListAuditQuery = Static<typeof ListAuditQuerySchema>

export const AuditLogPageSchema = createPageSchema(AuditLogSchema)
export type AuditLogPage = Static<typeof AuditLogPageSchema>

// Evento externo de la app — namespace obligatorio `app.*`; prohibido enviar secretos.
export const IngestAuditEventSchema = Type.Object({
  event: Type.String({ pattern: APP_EVENT_REGEX.source }),
  // Default: hora de recepción
  occurred_at: Type.Optional(IsoDateTimeSchema),
  actor_id: Type.Optional(Type.String()),
  target_type: Type.Optional(Type.String()),
  target_id: Type.Optional(Type.String()),
  data: Type.Optional(JsonRecordSchema),
})
export type IngestAuditEvent = Static<typeof IngestAuditEventSchema>

// POST /v1/audit/events [V1] — lote ≤ 100; requiere audit:write (M2M) o Bearer del actor.
export const IngestAuditRequestSchema = Type.Object({
  events: Type.Array(IngestAuditEventSchema, { minItems: 1, maxItems: 100 }),
})
export type IngestAuditRequest = Static<typeof IngestAuditRequestSchema>

// Detalle por evento del lote; 207 Multi-Status si parte del lote falla.
export const IngestAuditResultSchema = Type.Object({
  index: Type.Integer({ minimum: 0 }),
  status: Type.Union([Type.Literal("accepted"), Type.Literal("rejected")]),
  id: Type.Optional(Type.String()),
  error: Type.Optional(Type.String()),
})
export type IngestAuditResult = Static<typeof IngestAuditResultSchema>

export const IngestAuditResponseSchema = Type.Object({
  accepted: Type.Integer({ minimum: 0 }),
  rejected: Type.Integer({ minimum: 0 }),
  results: Type.Array(IngestAuditResultSchema),
})
export type IngestAuditResponse = Static<typeof IngestAuditResponseSchema>
